import Intercom from '@intercom/messenger-js-sdk';
import { update, shutdown, show, boot } from '@intercom/messenger-js-sdk';
import { User } from '@/types/user';
import { Integration } from '../types';

export interface IntercomConfig {
  appId: string;
}

/**
 * Create Intercom integration (synchronous)
 * Uses @intercom/messenger-js-sdk npm package
 *
 * Following Intercom's SPA best practices:
 * - Initialize with Intercom() default export at bootstrap
 * - Update with user data on login
 * - Shutdown then re-boot on logout to clear session
 *
 * Reference: https://developers.intercom.com/installing-intercom/web/methods
 */
export const createIntercomIntegration = (
  config: IntercomConfig
): Integration => {
  // Initialize Intercom SDK using default export
  // This is the standard way to initialize Intercom
  Intercom({
    api_base: 'https://api-iam.intercom.io',
    app_id: config.appId,
  });

  let isIdentified = false;

  return Object.freeze({
    name: 'intercom',

    identify: (user: User): void => {
      // Update existing Intercom instance with user information
      update({
        name: user.fullname,
        email: user.username,
        user_id: user.user_id,
        enterprise_id: user.enterprise_id,
        enterprise_name: user.enterprise_name,
      });

      isIdentified = true;
    },

    reset: (): void => {
      if (isIdentified) {
        // Clear user session and data
        shutdown();
        isIdentified = false;

        // IMPORTANT: Re-boot Intercom after shutdown for SPA
        // This prevents showing previous user's conversations on shared computers
        // and keeps the widget available for logged-out state
        boot({
          api_base: 'https://api-iam.intercom.io',
          app_id: config.appId,
        });
      }
    },
  });
};

/**
 * Show the Intercom messenger
 */
export const showIntercom = (): void => {
  show();
};
